import { useState, useCallback, useRef, useEffect } from "react";

import type { FormChatMessage } from "@kushya/shared";
import {
  fetchConversationSession,
  streamConversationMessage,
  type OrchestratorMetadata,
} from "@/api/respondent-conversation";
import { useSessionPersistence } from "./useSessionPersistence";

/**
 * Internal state for the conversation hook
 */
interface ConversationState {
  transcript: FormChatMessage[];
  isStreaming: boolean;
  streamingText: string;
  metadata: OrchestratorMetadata | null;
  responseId: string | null;
  error: string | null;
}

const INITIAL_STATE: ConversationState = {
  transcript: [],
  isStreaming: false,
  streamingText: "",
  metadata: null,
  responseId: null,
  error: null,
};

/**
 * Special message marker used to start a conversation.
 * Not displayed in transcript but satisfies backend validation.
 */
const START_CONVERSATION_MARKER = "__START__";

/**
 * Hook for managing respondent conversation state and streaming.
 * Handles SSE streaming, message accumulation, metadata updates,
 * and session persistence via localStorage.
 */
export function useRespondentConversation(
  projectId: string,
  formId: string,
  version: number,
) {
  const [state, setState] = useState<ConversationState>(INITIAL_STATE);
  const [isHydratingSession, setIsHydratingSession] = useState(false);
  const responseIdRef = useRef<string | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Session persistence
  const {
    sessionToken: storedSessionToken,
    responseId: storedResponseId,
    isRestoringSession,
    saveSession,
    clearSession,
  } = useSessionPersistence(projectId, formId, version);

  // Initialize refs from stored session
  useEffect(() => {
    if (storedResponseId && storedSessionToken) {
      responseIdRef.current = storedResponseId;
      sessionTokenRef.current = storedSessionToken;
      setState((prev) => ({
        ...prev,
        responseId: storedResponseId,
      }));
    }
  }, [storedResponseId, storedSessionToken]);

  // Hydrate transcript + metadata from the backend when restoring a session
  useEffect(() => {
    if (isRestoringSession) {
      return;
    }

    if (!storedResponseId || !storedSessionToken) {
      return;
    }

    let cancelled = false;
    setIsHydratingSession(true);

    void (async () => {
      try {
        const session = await fetchConversationSession({
          projectId,
          formId,
          version,
          sessionToken: storedSessionToken,
        });

        if (cancelled) {
          return;
        }

        setState((prev) => ({
          ...prev,
          transcript: session.transcript ?? [],
          metadata: session.metadata ?? null,
          responseId: session.responseId ?? storedResponseId,
          isStreaming: false,
          streamingText: "",
          error: null,
        }));
      } catch (error) {
        if (cancelled) {
          return;
        }

        const errorMessage =
          error instanceof Error ? error.message : "An unexpected error occurred";

        // Clear session if invalid/expired so the UI can start fresh
        if (
          errorMessage.includes("SESSION_TOKEN_EXPIRED") ||
          errorMessage.includes("SESSION_TOKEN_INVALID") ||
          errorMessage.includes("SESSION_REQUIRED") ||
          errorMessage.includes("SESSION_NOT_FOUND")
        ) {
          clearSession();
          responseIdRef.current = null;
          sessionTokenRef.current = null;
          setState(INITIAL_STATE);
        } else {
          setState((prev) => ({
            ...prev,
            error: errorMessage,
          }));
        }
      } finally {
        if (!cancelled) {
          setIsHydratingSession(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    clearSession,
    formId,
    isRestoringSession,
    projectId,
    storedResponseId,
    storedSessionToken,
    version,
  ]);

  /**
   * Send a message and stream the assistant's response
   */
  const sendMessage = useCallback(
    async (message: string) => {
      // Cancel any ongoing stream
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      // Check if this is the start marker (don't show in transcript)
      const isStartMarker = message === START_CONVERSATION_MARKER;

      // Add user message to transcript (skip for start marker)
      if (!isStartMarker && message.trim()) {
        const userMessage: FormChatMessage = {
          id: crypto.randomUUID(),
          role: "user",
          content: message,
          createdAt: new Date().toISOString(),
        };

        setState((prev) => ({
          ...prev,
          transcript: [...prev.transcript, userMessage],
          isStreaming: true,
          streamingText: "",
          error: null,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          isStreaming: true,
          streamingText: "",
          error: null,
        }));
      }

      try {
        for await (const event of streamConversationMessage({
          projectId,
          formId,
          version,
          message,
          responseId: responseIdRef.current ?? undefined,
          sessionToken: sessionTokenRef.current ?? undefined,
        })) {
          // Check for abort
          if (abortControllerRef.current?.signal.aborted) {
            break;
          }

          switch (event.type) {
            case "metadata":
              setState((prev) => ({
                ...prev,
                metadata: event.data,
              }));
              break;

            case "token":
              setState((prev) => ({
                ...prev,
                streamingText: prev.streamingText + event.text,
              }));
              break;

            case "complete":
              responseIdRef.current = event.responseId;

              // Save session if this is a new session (token provided)
              if (event.sessionToken) {
                sessionTokenRef.current = event.sessionToken;
                saveSession(event.responseId, event.sessionToken);
              }

              setState((prev) => ({
                ...prev,
                transcript: [...prev.transcript, event.assistantMessage],
                responseId: event.responseId,
                isStreaming: false,
                streamingText: "",
              }));
              break;
          }
        }
      } catch (error) {
        // Don't update state if aborted
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        const errorMessage =
          error instanceof Error ? error.message : "An unexpected error occurred";

        // Clear session on auth errors to allow fresh start
        if (
          errorMessage.includes("SESSION_TOKEN_EXPIRED") ||
          errorMessage.includes("SESSION_TOKEN_INVALID")
        ) {
          clearSession();
          responseIdRef.current = null;
          sessionTokenRef.current = null;
        }

        setState((prev) => ({
          ...prev,
          isStreaming: false,
          streamingText: "",
          error: errorMessage,
        }));
      }
    },
    [projectId, formId, version, saveSession, clearSession],
  );

  /**
   * Start the conversation by sending the start marker
   */
  const startConversation = useCallback(async () => {
    await sendMessage(START_CONVERSATION_MARKER);
  }, [sendMessage]);

  /**
   * Reset conversation state and clear stored session
   */
  const resetConversation = useCallback(() => {
    abortControllerRef.current?.abort();
    responseIdRef.current = null;
    sessionTokenRef.current = null;
    clearSession();
    setState(INITIAL_STATE);
  }, [clearSession]);

  /**
   * Dismiss the current error
   */
  const dismissError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Derived values from metadata
  const currentComponent = state.metadata?.componentType ?? null;
  const shouldRenderComponent = state.metadata?.shouldRenderComponent ?? false;
  const progress = state.metadata?.progressPercentage ?? 0;
  const isComplete = state.metadata?.isComplete ?? false;
  const currentQuestionRef = state.metadata?.currentQuestionRef ?? null;
  const phase = state.metadata?.phase ?? null;
  const answers = state.metadata?.answers ?? null;

  return {
    // State
    transcript: state.transcript,
    isStreaming: state.isStreaming,
    streamingText: state.streamingText,
    metadata: state.metadata,
    responseId: state.responseId,
    error: state.error,

    // Session state
    isRestoringSession: isRestoringSession || isHydratingSession,
    sessionToken: sessionTokenRef.current,

    // Derived values
    currentComponent,
    shouldRenderComponent,
    progress,
    isComplete,
    currentQuestionRef,
    phase,
    answers,

    // Actions
    sendMessage,
    startConversation,
    resetConversation,
    dismissError,
  };
}
